'use server'

import { createClient, createServiceClient } from '@repo/db/server'
import { normalizeInvitationEmail } from '@/lib/invitations'
import { hasInvitationMailer, invitationConfirmationUrl, invitationRequestUrl, sendInvitationMail } from '@/lib/invitations-mail'

export async function updateEmail(newEmail: string, currentPassword?: string) {
  const supabase = await createClient()
  const { data: { user }, error: authError } = await supabase.auth.getUser()
  if (authError || !user) return { error: 'Oturum bulunamadı' }

  if (!currentPassword) return { error: 'Mevcut şifrenizi girin.' }
  if (!user.email) return { error: 'Hesabın e-posta adresi bulunamadı.' }
  let email: string
  try { email = normalizeInvitationEmail(newEmail) } catch { return { error: 'Geçerli bir e-posta adresi gerekli.' } }
  if (email === user.email.trim().toLowerCase()) {
    return { error: 'Yeni e-posta mevcut e-posta ile aynı.' }
  }

  const { data: verified, error: passwordError } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword })
  if (passwordError || verified.user?.id !== user.id) return { error: 'Mevcut şifre hatalı.' }
  if (!hasInvitationMailer()) return { error: 'E-posta gönderimi yapılandırılmamış. Adres değiştirilmedi.' }
  try {
    const admin = createServiceClient()
    const origin = await invitationRequestUrl('/')
    // SMTP ve GoTrue redirect izin listesinden bağımsız SSR token doğrulaması.
    const next = await admin.auth.admin.generateLink({ type: 'email_change_new', email: user.email, newEmail: email })
    if (next.error || next.data.user?.id !== user.id || !next.data.properties?.hashed_token) throw new Error('Doğrulama oluşturulamadı.')
    const confirmationUrl = invitationConfirmationUrl(origin, next.data.properties.hashed_token, 'email_change')
    // GoTrue secure-email-change varsayılanı çift onaydır. Mevcut parola aynı
    // UID için az önce doğrulandı: eski adres aşaması sunucuda tamamlanır;
    // eski adrese token gönderilmez, yeni adres hâlâ kullanıcı linkini bekler.
    const current = await admin.auth.admin.generateLink({ type: 'email_change_current', email: user.email, newEmail: email })
    if (current.error) {
      // Secure-email-change kapalı kurulumda yeni adres linki tek başına yeter.
      // Diğer provider hataları fail-closed kalır.
      if (current.error.code !== 'validation_failed' || current.error.message !== 'Enable secure email change to generate link for current email') throw new Error('Mevcut adres doğrulanamadı.')
    } else {
      if (current.data.user?.id !== user.id || !current.data.properties?.hashed_token) throw new Error('Mevcut adres doğrulanamadı.')
      const confirmation = await supabase.auth.verifyOtp({ type: 'email_change', token_hash: current.data.properties.hashed_token })
      if (confirmation.error) throw new Error('Mevcut adres doğrulanamadı.')
    }
    await sendInvitationMail(email, 'Yeni e-posta adresinizi doğrulayın', `Yeni adresinizi doğrulamak için:\n${confirmationUrl}`)
    await sendInvitationMail(user.email, 'E-posta değişikliği istendi', 'Hesabınız için e-posta değişikliği istendi. Yeni adres doğrulanmadan mevcut adresiniz değişmez. Bu işlemi siz başlatmadıysanız hesabınızın güvenliğini kontrol edin.')
    return { success: true, message: 'Yeni adresinize doğrulama bağlantısı gönderildi. E-posta adresiniz doğrulama tamamlandıktan sonra değişir.' }
  } catch {
    return { error: 'Doğrulama e-postası gönderilemedi. Adres değişimi tamamlanmadı; daha sonra yeniden deneyin.' }
  }
}

export async function setInvitationPassword(newPassword: string, confirmation: string) {
  const supabase = await createClient()
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error || !user?.email_confirmed_at || !user.invited_at || user.app_metadata?.gs29_password_pending !== true) {
    return { error: 'Parola belirlemek için davet bağlantısını doğrulayın.' }
  }
  if (newPassword.length < 8 || newPassword.length > 128) return { error: 'Şifre 8–128 karakter olmalıdır.' }
  if (newPassword !== confirmation) return { error: 'Şifreler eşleşmiyor.' }
  const { error: passwordError } = await supabase.auth.updateUser({ password: newPassword })
  if (passwordError) return { error: 'Şifre kaydedilemedi. Yeniden deneyin.' }
  // İstemci user_metadata'sı bu yetkiyi açamaz. Yalnız yeni davette service
  // tarafından konan app_metadata bayrağı, aynı Auth UID için kaldırılır.
  const { error: flagError } = await createServiceClient().auth.admin.updateUserById(user.id, { app_metadata: { gs29_password_pending: false } })
  if (flagError) return { error: 'Şifre kaydedildi fakat davet tamamlanamadı. Yeniden deneyin.' }
  return { success: true, message: 'Şifreniz kaydedildi. Panele giriş yapabilirsiniz.' }
}

export async function updatePassword(currentPassword: string, newPassword: string) {
  const supabase = await createClient()
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { error: 'Oturum bulunamadı' }

  if (newPassword.length < 6) {
    return { error: 'Şifre en az 6 karakter olmalıdır.' }
  }

  // Mevcut şifreyi doğrula
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: user.email!,
    password: currentPassword,
  })

  if (signInError) {
    return { error: 'Mevcut şifre hatalı.' }
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) return { error: error.message }

  return { success: true, message: 'Şifreniz başarıyla güncellendi.' }
}
